// Картинки статьи открываются крупно в PhotoSwipe: соседние листаются стрелками и свайпом,
// увеличиваются пальцами, окно закрывается и кнопкой «назад», не уходя со страницы.
// Ядро PhotoSwipe — вторая сборка rollup (assets/built/photoswipe.js и .css) — грузится
// только при первом открытии, поэтому на скорость страницы не влияет.
// markZoomableImages() вызывается на каждой странице (и после подмены через PJAX),
// lightbox() — один раз за сессию.

const ZOOMABLE_IMAGES = '.gh-content .kg-image-card img, .gh-content .kg-gallery-image img';
const ZOOMABLE = '[data-th-zoomable]';
const MAIN_BUNDLE = 'script[src*="built/index.js"]';
const LOAD_TIMEOUT_MS = 10000;
const HISTORY_FLAG = 'thLightbox';
const REDUCED_MOTION = '(prefers-reduced-motion: reduce)';
// Стандартные 0.8 PhotoSwipe на светлой странице просвечивают: текст статьи спорит с подписью.
const BACKDROP_OPACITY = 0.94;

let corePromise = null;
let openGallery = null;
let opening = false;

/** Помечает картинки статьи, которые открываются крупно, и даёт им фокус с клавиатуры. */
export function markZoomableImages() {
    document.querySelectorAll(ZOOMABLE_IMAGES).forEach((img) => {
        // Картинка-ссылка ведёт туда, куда её поставил редактор, — лайтбокс её не перехватывает.
        if (img.closest('a')) {
            return;
        }
        img.dataset.thZoomable = '';
        img.tabIndex = 0;
        img.setAttribute('role', 'button');
        img.setAttribute('aria-haspopup', 'dialog');
        if (!img.alt) {
            img.setAttribute('aria-label', 'Open image full size');
        }
    });
}

/** Слушает нажатия на помеченные картинки и кнопку «назад» при открытом окне. */
export default function lightbox() {
    document.addEventListener('click', onClick);
    document.addEventListener('keydown', onKeydown);
    window.addEventListener('popstate', closeOnBack);
}

function onClick(e) {
    if (e.button !== 0 || !(e.target instanceof Element)) {
        return;
    }
    const img = e.target.closest(ZOOMABLE);
    if (!img) {
        return;
    }
    e.preventDefault();
    open(img);
}

function onKeydown(e) {
    if (e.key !== 'Enter' && e.key !== ' ') {
        return;
    }
    if (!(e.target instanceof Element) || !e.target.matches(ZOOMABLE)) {
        return;
    }
    e.preventDefault();
    open(e.target);
}

function closeOnBack() {
    if (openGallery) {
        openGallery.close();
    }
}

/** Открывает все помеченные картинки статьи одной лентой, начиная с нажатой. */
async function open(img) {
    if (opening || openGallery) {
        return;
    }
    opening = true;
    try {
        const images = [...img.closest('.gh-content').querySelectorAll(ZOOMABLE)];
        const [PhotoSwipe, slides] = await Promise.all([
            loadCore(),
            Promise.all(images.map(slideFrom)),
        ]);
        const pswp = new PhotoSwipe({
            dataSource: slides,
            index: images.indexOf(img),
            bgOpacity: BACKDROP_OPACITY,
            // Читателю, который просил систему убрать движение, окно открывается без зума.
            showHideAnimationType: matchMedia(REDUCED_MOTION).matches ? 'none' : 'zoom',
        });

        registerCaption(pswp);
        closeWithHistory(pswp);
        pswp.init();
    } catch (error) {
        // Окно не открылось — читатель всё равно получает картинку крупно, обычным переходом.
        console.error('Lightbox failed, opening the image itself:', error);
        corePromise = null;
        location.href = img.src;
    } finally {
        opening = false;
    }
}

/** Данные слайда PhotoSwipe: оригинал, srcset, размеры и миниатюра для анимации открытия. */
async function slideFrom(img) {
    const { width, height } = await sizeOf(img);

    return { src: img.src, srcset: img.srcset, width, height, alt: img.alt, element: img };
}

// Ghost пишет размеры оригинала в атрибуты; без них — настоящие размеры загруженного файла.
async function sizeOf(img) {
    const width = Number(img.getAttribute('width'));
    const height = Number(img.getAttribute('height'));
    if (width > 0 && height > 0) {
        return { width, height };
    }
    await img.decode();
    return { width: img.naturalWidth, height: img.naturalHeight };
}

/** Подпись под картинкой в окне — копия её figcaption, ссылки в ней остаются рабочими. */
function registerCaption(pswp) {
    pswp.on('uiRegister', () => {
        pswp.ui.registerElement({
            name: 'th-caption',
            appendTo: 'root',
            isButton: false,
            onInit: (element) => {
                pswp.on('change', () => fillCaption(element, pswp.currSlide.data.element));
            },
        });
    });
}

function fillCaption(element, img) {
    const caption = img.closest('figure')?.querySelector('figcaption');
    element.hidden = !caption;
    element.replaceChildren(...(caption ? caption.cloneNode(true).childNodes : []));
}

// Открытое окно — отдельный шаг истории на том же адресе: «назад» на телефоне закрывает
// окно, а не уходит со статьи. pjax.js такой шаг не трогает — адрес страницы тот же.
function closeWithHistory(pswp) {
    history.pushState({ ...history.state, [HISTORY_FLAG]: true }, '');
    openGallery = pswp;
    pswp.on('destroy', () => {
        openGallery = null;
        if (history.state?.[HISTORY_FLAG]) {
            history.back();
        }
    });
}

/** Загружает ядро PhotoSwipe и его стили один раз; возвращает класс PhotoSwipe. */
function loadCore() {
    if (!corePromise) {
        corePromise = Promise.all([
            withTimeout(import(coreUrl('js')), 'photoswipe.js'),
            withTimeout(loadStylesheet(coreUrl('css')), 'photoswipe.css'),
        ]).then(([module]) => module.default);
    }
    return corePromise;
}

// Адрес строится от адреса основного бандла — с той же меткой версии ?v= из {{asset}}.
function coreUrl(extension) {
    const bundle = document.querySelector(MAIN_BUNDLE);
    if (!bundle) {
        throw new Error('main bundle script not found');
    }
    return bundle.src.replace('/built/index.js', `/built/photoswipe.${extension}`);
}

function loadStylesheet(href) {
    return new Promise((resolve, reject) => {
        const link = document.createElement('link');

        link.rel = 'stylesheet';
        link.href = href;
        link.onload = resolve;
        link.onerror = () => {
            link.remove();
            reject(new Error(`${href}: failed to load`));
        };
        document.head.append(link);
    });
}

function withTimeout(promise, name) {
    let timer;
    const timeout = new Promise((resolve, reject) => {
        timer = setTimeout(() => reject(new Error(`${name}: no response in ${LOAD_TIMEOUT_MS} ms`)), LOAD_TIMEOUT_MS);
    });

    return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}
